import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { cva } from "class-variance-authority";

import { cn } from "../../../../lib/utils";

// Field의 기본 Props 타입 정의
export interface FieldRootProps
  extends React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Root> {
  children: React.ReactNode;
}

// Field 컴포넌트를 Popover 기반으로 변경
const Field = PopoverPrimitive.Root;

// FieldGroup 컴포넌트
const FieldGroup = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex items-center", className)} {...props} />
));
FieldGroup.displayName = "FieldGroup";

// FieldTrigger를 PopoverTrigger 기반으로 변경
const FieldTrigger = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Trigger> & {
    selectedCount?: number;
    onClear?: () => void;
    open?: boolean;
  }
>(({ className, children, selectedCount, onClear, open, ...props }, ref) => (
  <PopoverPrimitive.Trigger
    ref={ref}
    className={cn(
      "focus-visible:outline-none",
      "transition-all duration-200",
      "rounded-sm",
      className
    )}
    {...props}
  >
    {children}
  </PopoverPrimitive.Trigger>
));
FieldTrigger.displayName = PopoverPrimitive.Trigger.displayName;

// FieldContent를 PopoverContent 기반으로 변경
const FieldContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & {
    disabled?: boolean;
    align?: "start" | "center" | "end";
  }
>(
  (
    {
      className,
      children,
      disabled,
      align = "center",
      sideOffset = 4,
      ...props
    },
    ref
  ) => (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        ref={ref}
        align={align}
        sideOffset={sideOffset}
        className={cn(
          "relative z-50 max-h-96 overflow-hidden rounded-md bg-semantic-secondary-background-strong-default text-semantic-secondary-foreground-normal-default shadow-focus font-normal flex flex-col gap-2 py-1",
          "min-w-[--radix-popover-trigger-width] w-fit",
          "data-[side=top]:border-b-2 data-[side=top]:border-semantic-primary-stroke-global-default",
          "data-[side=bottom]:border-t-2 data-[side=bottom]:border-semantic-primary-stroke-global-default",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          "data-[side=bottom]:slide-in-from-top-2",
          "data-[side=left]:slide-in-from-right-2",
          "data-[side=right]:slide-in-from-left-2",
          "data-[side=top]:slide-in-from-bottom-2",
          className
        )}
        style={{ pointerEvents: disabled ? "none" : "auto" }}
        {...props}
      >
        {children}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  )
);
FieldContent.displayName = PopoverPrimitive.Content.displayName;

// FieldContentItem Props 타입 정의
interface FieldContentItemProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "filled" | "outlined" | "transparent";
  selected?: boolean;
}

// FieldContentItem 스타일 정의
const fieldContentItemVariants = cva(
  "relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 outline-none typography-semantic-label-1-normal---regular",
  {
    variants: {
      variant: {
        filled: "hover:bg-semantic-secondary-background-normal-hovered",
        outlined: "hover:bg-semantic-secondary-background-heavy-hovered",
        transparent: "hover:bg-semantic-secondary-background-heavy-hovered",
      },
    },
    defaultVariants: {
      variant: "filled",
    },
  }
);

// FieldContentItem 컴포넌트 수정
const FieldContentItem = React.forwardRef<
  HTMLDivElement,
  FieldContentItemProps
>(({ className, children, variant, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(fieldContentItemVariants({ variant, className }))}
    {...props}
  >
    {children}
  </div>
));
FieldContentItem.displayName = "FieldContentItem";

// FieldClose Props 타입 정의
interface FieldCloseProps extends React.HTMLAttributes<HTMLDivElement> {
  onClose?: () => void;
}

// FieldClose 컴포넌트 구현
const FieldClose = React.forwardRef<HTMLDivElement, FieldCloseProps>(
  ({ className, children, onClose, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn("cursor-pointer", className)}
        onClick={(e) => {
          e.stopPropagation();
          onClose?.();
        }}
        {...props}
      >
        {children}
      </div>
    );
  }
);
FieldClose.displayName = "FieldClose";

export {
  Field,
  FieldGroup,
  FieldTrigger,
  FieldContent,
  FieldClose,
  FieldContentItem,
};
